import { Home, Palmtree, Utensils, Sparkle, Car, Music, ArrowRight } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"

const categories = [
  {
    icon: Home,
    title: "Accommodation",
    description: "Save up to 30% when providers compete for your booking",
    tagline: "Hotels, apartments, unique stays",
    href: "/categories/accommodation",
    image: "/images/3.jpg",
    gradient: "from-blue-500/20 to-purple-500/20",
  },
  {
    icon: Palmtree,
    title: "Safaris & Adventures",
    description: "Verified offers from top regional safari operators",
    tagline: "Wildlife tours, outdoor experiences",
    href: "/categories/safari",
    image: "/images/7.jpg",
    gradient: "from-green-500/20 to-emerald-500/20",
  },
  {
    icon: Utensils,
    title: "Restaurants",
    description: "Reserve tables at your preferred price",
    tagline: "Fine dining, local cuisine, cafes",
    href: "/categories/restaurants",
    image: "/images/4.jpg",
    gradient: "from-orange-500/20 to-red-500/20",
  },
  {
    icon: Music,
    title: "Nightlife",
    description: "VIP access and bottle service at negotiable rates",
    tagline: "Clubs, bars, live entertainment",
    href: "/categories/nightlife",
    image: "/images/2.jpg",
    gradient: "from-pink-500/20 to-purple-500/20",
  },
  {
    icon: Sparkle,
    title: "Wellness & Beauty",
    description: "Spa treatments and salon services on demand",
    tagline: "Spa, beauty, health services",
    href: "/categories/wellness",
    image: "/images/6.jpg",
    gradient: "from-rose-500/20 to-pink-500/20",
  },
  {
    icon: Car,
    title: "Car Hire & Transport",
    description: "Rent vehicles from verified providers at negotiable rates",
    tagline: "Cars, transfers, chauffeurs",
    href: "/categories/transport",
    image: "/images/11.png",
    gradient: "from-indigo-500/20 to-blue-500/20",
  },
]

export function ServiceCategories() {
  return (
    <section className="py-20 md:py-32 bg-gradient-to-b from-white via-gray-50/50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#DB4789]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#75055B]/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#DB4789]/10 rounded-full mb-6">
              <span className="text-sm font-semibold text-[#75055B]">All Services in One Place</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-black via-[#75055B] to-black bg-clip-text text-transparent">
              Explore Services
            </h2>
            <p className="text-xl sm:text-2xl text-[#75055B] font-semibold mb-4">
              Save up to 30% when providers compete for your price
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              One app, every service — all with prices you control
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {categories.map((category, index) => (
              <Link to={category.href} key={index}>
                <Card className="group cursor-pointer overflow-hidden border-2 border-gray-200 hover:border-[#DB4789] transition-all duration-500 hover:shadow-2xl hover:shadow-[#DB4789]/20 relative h-[420px] sm:h-[480px] bg-white">
                  {/* Image with overlay */}
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={category.image || "/placeholder.svg"}
                      alt={category.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/40 group-hover:from-black/98 group-hover:via-black/80 transition-all duration-500 ${category.gradient}`} />
                  </div>

                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-end p-8 text-white">
                    {/* Icon */}
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 group-hover:bg-[#DB4789] group-hover:border-[#DB4789] group-hover:scale-110 transition-all duration-500 mb-6 shadow-lg">
                      <category.icon className="w-8 h-8" />
                    </div>

                    {/* Tagline */}
                    <div className="text-xs uppercase tracking-widest text-[#DB4789] font-bold mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {category.tagline}
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl sm:text-3xl font-bold mb-3 group-hover:text-[#DB4789] transition-colors duration-300">
                      {category.title}
                    </h3>

                    {/* Description */}
                    <p className="text-base text-white/90 leading-relaxed mb-6 line-clamp-2">
                      {category.description}
                    </p>

                    {/* CTA */}
                    <div className="flex items-center text-white group-hover:text-[#DB4789] font-semibold transition-colors duration-300">
                      <span>Explore</span>
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>

                  {/* Hover effect border */}
                  <div className="absolute inset-0 border-2 border-[#DB4789] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-[#75055B] to-[#DB4789] text-white hover:from-[#DB4789] hover:to-[#75055B] px-10 py-7 text-lg rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-[#DB4789]/30 transition-all duration-300 font-semibold group"
            >
              View All Services
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
